/**
 * Dev-only results-gate override — "Full Results" in the Dev Console.
 *
 * Restores what the old 🔓/🔒 button did before `4d5c758`: let a developer see
 * the whole estimate without filling in the guest capture form. That button
 * worked by calling `devToggleAuth()`, which mocked a logged-in user; SSO
 * integration rewired it to a real Keycloak `login()` redirect and the local
 * flip was lost.
 *
 * This deliberately does NOT fake authentication. `isAuthenticated` now drives
 * auto-save, profile fetches and session-bearing API calls, so faking it would
 * produce a broken half-session rather than a preview. Instead it flips one
 * boolean that only widens the client-side results gate.
 *
 * Not a hole: that gate was never a security control — the server re-resolves
 * and re-enforces the capture policy on every save — and the consumer ANDs this
 * with `isSuperAdmin` regardless. sessionStorage, so it dies with the tab and is
 * cleared by the Dev Console's own "Clear Session".
 */

import { useSyncExternalStore } from 'react';

const KEY = 'dev:unlockResults';
const EVENT = 'dev-unlock-results-changed';

export function isResultsUnlocked(): boolean {
  try {
    return sessionStorage.getItem(KEY) === '1';
  } catch {
    return false; // private mode / blocked storage
  }
}

export function setResultsUnlocked(on: boolean): void {
  try {
    if (on) sessionStorage.setItem(KEY, '1');
    else sessionStorage.removeItem(KEY);
  } catch {
    /* ignore — the event below still drives this tab's render */
  }
  window.dispatchEvent(new Event(EVENT));
}

function subscribe(onChange: () => void): () => void {
  window.addEventListener(EVENT, onChange);
  return () => window.removeEventListener(EVENT, onChange);
}

/** Live view of the override. Re-renders the caller when it is toggled. */
export function useResultsUnlocked(): boolean {
  return useSyncExternalStore(subscribe, isResultsUnlocked, () => false);
}
